<template>
    <div class="card mb-25 border-0 bg-white letter-spacing" v-if="commande">
        <div class="card-header d-flex align-items-center justify-content-between">
            <h3 class="text-black fw-semibold">
                Commande fournisseur {{ commande.refCom }}
                <span class="badge ms-2" :class="classeStatut(commande.statut)">{{ libelleStatut(commande.statut)
                }}</span>
            </h3>
        </div>

        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
            <div class="row mb-3">
                <div class="col-md-3 mb-2">
                    <div class="text-body-tertiary fs-13 text-uppercase">Date</div>
                    <div class="text-black fw-semibold">{{ format_date(commande.dateComFrs) }}</div>
                </div>
                <div class="col-md-3 mb-2">
                    <div class="text-body-tertiary fs-13 text-uppercase">Fournisseur</div>
                    <div class="text-black fw-semibold">{{ libelleFournisseur(commande.fournisseur) }}</div>
                </div>
                <div class="col-md-3 mb-2">
                    <div class="text-body-tertiary fs-13 text-uppercase">Point de vente</div>
                    <div class="text-black fw-semibold">{{ commande.pointvente?.nomPointVente }}</div>
                </div>
                <div class="col-md-3 mb-2">
                    <div class="text-body-tertiary fs-13 text-uppercase">Poids total</div>
                    <div class="text-black fw-semibold">{{ formatPoids(commande.poidsTotal) }}</div>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-md-3 mb-2">
                    <div class="text-body-tertiary fs-13 text-uppercase">Conducteur</div>
                    <div class="text-black fw-semibold">{{ commande.nomConducteur || '-' }}</div>
                </div>
                <div class="col-md-3 mb-2">
                    <div class="text-body-tertiary fs-13 text-uppercase">Téléphone du conducteur</div>
                    <div class="text-black fw-semibold">{{ commande.telephoneConducteur || '-' }}</div>
                </div>
                <div class="col-md-3 mb-2">
                    <div class="text-body-tertiary fs-13 text-uppercase">Immatriculation du véhicule</div>
                    <div class="text-black fw-semibold">{{ commande.immatriculationVehicule || '-' }}</div>
                </div>
            </div>

            <div class="border border-primary mb-3">
                <div class="row py-2 bg-primary mx-0">
                    <h4 class="fs-4 text-white mb-0">Les produits</h4>
                </div>
                <div class="table-responsive">
                    <table class="table align-middle mb-0">
                        <thead>
                            <tr>
                                <th class="text-uppercase fw-medium text-body-tertiary fs-13">Produit</th>
                                <th class="text-uppercase fw-medium text-body-tertiary fs-13 col-description">Description</th>
                                <th class="text-uppercase fw-medium text-body-tertiary fs-13 text-nowrap">Quantité</th>
                                <th class="text-uppercase fw-medium text-body-tertiary fs-13 text-nowrap">Poids</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="ligne in lignes" :key="ligne.id">
                                <td class="fw-medium">{{ ligne.nomProd }}</td>
                                <td class="fw-medium description-cell">{{ ligne.description || '-' }}</td>
                                <td class="fw-medium text-nowrap">{{ ligne.qteComfrs }}</td>
                                <td class="fw-medium text-nowrap">{{ formatPoids(ligne.poids) }}</td>
                            </tr>
                            <tr v-if="lignes.length === 0">
                                <td colspan="4" class="text-center text-body-tertiary py-3">Aucun produit.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="d-flex align-items-center">
                <template v-if="estEnAttente">
                    <router-link v-if="checkPermission('EditCommandeFournisseur')"
                        :to="{ name: 'EditCommandeFournisseurPage', params: { id: commande.id } }"
                        class="btn btn-primary me-3">
                        <i class="flaticon-pen lh-1 me-1 position-relative top-1"></i>Modifier
                    </router-link>
                    <button v-if="checkPermission('ValiderCommandeFournisseur')" type="button"
                        class="btn btn-success me-3" @click="valider">
                        <i class="fa fa-check lh-1 me-1"></i>Valider
                    </button>
                    <button v-if="checkPermission('RejeterCommandeFournisseur')" type="button"
                        class="btn btn-danger me-3" @click="rejeter">
                        <i class="fa fa-ban lh-1 me-1"></i>Rejeter
                    </button>
                </template>
                <router-link :to="{ name: 'ListCommandesFournisseursPage' }" class="btn btn-secondary">
                    Retour à la liste
                </router-link>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import ApiService from '@/services/ApiService';
import JwtService from '@/services/JwtService';
import { format_date, error, success } from '@/utils/utils';
import Swal from 'sweetalert2';

const STATUT_EN_ATTENTE = "en attente de validation";
const STATUT_VALIDE = "valide";
const STATUT_REJETE = "rejete";

export default defineComponent({
    name: "ViewCommandeFournisseur",

    setup: () => {
        const route = useRoute();
        const commandeId = route.params.id as string;

        const commande = ref<any>(null);
        const privileges = ref<Array<string>>(JwtService.getPrivilege());

        const checkPermission = (name: string) => {
            return privileges.value.includes(name);
        };

        const lignes = computed(() => {
            if (commande.value && commande.value.commandefourniseurprocondis) {
                return commande.value.commandefourniseurprocondis;
            } else {
                return [];
            }
        });

        const estEnAttente = computed(() => {
            if (commande.value && commande.value.statut === STATUT_EN_ATTENTE) {
                return true;
            } else {
                return false;
            }
        });

        const charger = async () => {
            try {
                const response = await ApiService.get(`/commandesFournisseurs/${commandeId}`);
                commande.value = response.data.data;
            } catch (err: any) {
                error(err.response?.data?.message || "Erreur lors du chargement de la commande");
            }
        };

        const libelleStatut = (statut: string): string => {
            if (statut === STATUT_VALIDE) {
                return "Validée";
            } else if (statut === STATUT_REJETE) {
                return "Rejetée";
            } else {
                return "En attente de validation";
            }
        };

        const classeStatut = (statut: string): string => {
            if (statut === STATUT_VALIDE) {
                return "badge-success";
            } else if (statut === STATUT_REJETE) {
                return "badge-danger";
            } else {
                return "badge-warning";
            }
        };

        const libelleFournisseur = (f: any): string => {
            if (!f) {
                return '';
            }
            if (f.statut === 2 || f.statut === "2") {
                return `${f.sigle} - ${f.denomination}`;
            }
            if (f.nomFournisseur && f.prenomFournisseur) {
                return `${f.nomFournisseur} ${f.prenomFournisseur}`;
            }
            return `${f.denomination || f.nomFournisseur || ''}`;
        };

        const formatPoids = (poids: any): string => {
            if (poids === null || poids === undefined || poids === '') {
                return '-';
            }
            return Number(poids).toLocaleString('fr-FR', { maximumFractionDigits: 3 });
        };

        const confirmer = async (texte: string, libelleBouton: string): Promise<boolean> => {
            const result = await Swal.fire({
                text: texte,
                icon: "warning",
                buttonsStyling: true,
                showCancelButton: true,
                confirmButtonText: libelleBouton,
                cancelButtonText: "Annuler",
                heightAuto: false,
                customClass: {
                    confirmButton: "btn btn-primary",
                    cancelButton: "btn btn-danger"
                },
            });
            return result.isConfirmed;
        };

        const valider = async () => {
            const confirme = await confirmer(`Vous êtes sur le point de valider la commande ${commande.value.refCom}.\nEtes-vous sûr ?`, "Valider");
            if (!confirme) {
                return;
            }
            try {
                const response = await ApiService.put(`/commandesFournisseurs/${commandeId}/valider`, {});
                success(response.data.message);
                charger();
            } catch (err: any) {
                error(err.response?.data?.message || "Erreur lors de la validation de la commande");
            }
        };

        const rejeter = async () => {
            const confirme = await confirmer(`Vous êtes sur le point de rejeter la commande ${commande.value.refCom}.\nEtes-vous sûr ?`, "Rejeter");
            if (!confirme) {
                return;
            }
            try {
                const response = await ApiService.put(`/commandesFournisseurs/${commandeId}/rejeter`, {});
                success(response.data.message);
                charger();
            } catch (err: any) {
                error(err.response?.data?.message || "Erreur lors du rejet de la commande");
            }
        };

        onMounted(() => {
            charger();
        });

        return {
            commande,
            lignes,
            estEnAttente,
            checkPermission,
            format_date,
            formatPoids,
            libelleFournisseur,
            libelleStatut,
            classeStatut,
            valider,
            rejeter,
        };
    },
});
</script>

<style scoped>
    .col-description {
        width: 40%;
    }

    .description-cell {
        white-space: pre-line;      /* respecte les retours à la ligne saisis dans le textarea */
        overflow-wrap: anywhere;    /* coupe aussi les mots très longs sans espace */
        word-break: break-word;
    }
</style>